<template>
  <div class="cheque-print">
    <div class="controls d-print-none">
      <button class="btn btn-secondary mx-2" @click="$router.go(-1)">
        <i class="las la-arrow-left me-1"></i>
        Back
      </button>
      <button class="btn btn-primary" @click="printCheque">
        <i class="ri-printer-cloud-line align-bottom me-1"></i>
        Print
      </button>
    </div>

    <div v-if="loading" class="card">
      <LoadingModal />
    </div>
    <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

    <div v-else-if="payment" class="cheque-wrapper">
      <div class="cheque-surface" id="cheque-sheet">
        <div class="header-row">
          <div class="payer-block">
            <p class="payer-name">{{ payer || "Payer" }}</p>
            <p
              v-for="(line, idx) in payerAddressLines"
              :key="`payer-address-${idx}`"
              class="payer-address"
            >
              {{ line }}
            </p>
          </div>

          <div class="date-block">
            <div class="cheque-number">{{ formattedChequeNo || "------" }}</div>
            <div class="date-line">
              <span class="label">Date:</span>
              <span class="value dotted">{{
                formattedDate || "MM/DD/YYYY"
              }}</span>
            </div>
          </div>
        </div>

        <div class="payee-line">
          <div class="payee-label">Pay to the Order of</div>
          <div class="payee-value underline">
            <strong>{{ payee || "" }}</strong>
          </div>
          <div class="amount-box">
            <strong>
              <span class="currency">$</span>
              <span class="amount">{{ formattedAmount }}</span>
            </strong>
          </div>
        </div>

        <div class="amount-words underline">
          <strong>
            <span class="amount-text">{{ amountInWords }}</span>
            <span class="dollars-label">&nbsp;Dollars</span>
          </strong>
        </div>

        <div class="bank-row">
          <p class="bank-name">{{ payment.bankName || "" }}</p>
          <p
            v-for="(line, idx) in bankAddressLines"
            :key="`bank-address-${idx}`"
            class="bank-address"
          >
            {{ line }}
          </p>
        </div>

        <div class="footer-row">
          <div class="memo-block">
            <span class="label">Memo:&nbsp;</span>
            <span class="memo-line">{{ memoText }}</span>
          </div>

          <div class="signature-block">
            <span class="signature-line"></span>
            <span class="signature-name">Authorized Signature</span>
          </div>
        </div>

        <!-- MICR LINE -->
        <div class="micr-row">
          <div class="micr" style="display: inline">
            c{{ formattedChequeNo || "" }}c
          </div>
          <div class="micr" style="display: inline">
            a{{ payment.routingNo || "" }}a
          </div>
          <div class="micr" style="display: inline">
            {{ payment.accountNo || "" }}c
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingModal from "@/components/utils/LoadingModal";

/* --------------------------------------------------------------------------
   Number Conversion Helpers (EXACT as reference)
-------------------------------------------------------------------------- */

const LESS_THAN_TWENTY = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
];

const TENS = [
  "",
  "Ten",
  "Twenty",
  "Thirty",
  "Forty",
  "Fifty",
  "Sixty",
  "Seventy",
  "Eighty",
  "Ninety",
];

const THOUSANDS = ["", "Thousand", "Million", "Billion"];

function convertHundreds(num) {
  let str = "";

  if (num >= 100) {
    str += `${LESS_THAN_TWENTY[Math.floor(num / 100)]} Hundred `;
    num %= 100;
  }

  if (num >= 20) {
    str += `${TENS[Math.floor(num / 10)]} `;
    num %= 10;
  }

  if (num > 0) {
    str += `${LESS_THAN_TWENTY[num]} `;
  }

  return str.trim();
}

function numberToWords(num) {
  if (num === 0) return "Zero";

  let i = 0;
  let words = "";

  while (num > 0) {
    if (num % 1000 !== 0) {
      words = `${convertHundreds(num % 1000)} ${THOUSANDS[i]} ${words}`.trim();
    }
    num = Math.floor(num / 1000);
    i++;
  }

  return words.trim();
}

function currencyToWords(value) {
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return "";

  const [whole, decimals = "00"] = numericValue.toFixed(2).split(".");
  const wholeWords = numberToWords(Number(whole));

  return `${wholeWords} And ${decimals}/100`;
}

/* --------------------------------------------------------------------------
   EXPORT COMPONENT
-------------------------------------------------------------------------- */

export default {
  name: "DistributorChequePrintView",
  components: { LoadingModal },

  computed: {
    storedPayment() {
      return this.$store.getters.selectedDistributorPayment;
    },

    payment() {
      return this.storedPayment;
    },

    loading() {
      return this.$store.getters.distributorPaymentHistoryLoading;
    },

    error() {
      return this.$store.getters.distributorPaymentHistoryError;
    },

    payee() {
      if (!this.payment) return "";
      return this.payment.payee || this.payment.distributor || "";
    },

    payer() {
      if (!this.payment) return "";
      return this.payment.payer || "";
    },

    bankAddressLines() {
      if (!this.payment || !this.payment.bankAddress) {
        return ["", ""];
      }
      const addr = this.payment.bankAddress;
      const idx = addr.indexOf(",");
      if (idx === -1) {
        return [addr.trim(), ""];
      }
      const line1 = addr.slice(0, idx).trim();
      const line2 = addr.slice(idx + 1).trim();
      return [line1, line2];
    },

    payerAddressLines() {
      if (!this.payment || !this.payment.payerAddress) {
        return ["", ""];
      }
      const addr = this.payment.payerAddress;
      const idx = addr.indexOf(",");
      if (idx === -1) {
        return [addr.trim(), ""];
      }
      const line1 = addr.slice(0, idx).trim();
      const line2 = addr.slice(idx + 1).trim();
      return [line1, line2];
    },

    formattedChequeNo() {
      if (
        !this.payment ||
        this.payment.chequeNo === null ||
        this.payment.chequeNo === undefined
      ) {
        return "";
      }
      return this.payment.chequeNo.toString().padStart(6, "0");
    },

    formattedAmount() {
      if (!this.payment) return "0.00";
      return Number(this.payment.amountPaid || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formattedDate() {
      if (!this.payment || !this.payment.date) return "";
      const [year, month, day] = this.payment.date.split("-");
      return `${month}/${day}/${year}`;
    },

    amountInWords() {
      if (!this.payment) return "";
      return currencyToWords(this.payment.amountPaid || 0);
    },

    memoText() {
      if (!this.payment) return "";
      return this.payment.invoiceNo || "";
    },
  },

  methods: {
    async fetchPayment() {
      await this.$store.dispatch(
        "getDistributorPayment",
        this.$route.params.id
      );
    },

    printCheque() {
      window.print();
    },
  },

  async created() {
    await this.fetchPayment();
  },

  beforeUnmount() {
    this.$store.commit("setSelectedDistributorPayment", null);
  },
};
</script>

<style scoped>
.cheque-print {
  padding: 20px;
}

.controls {
  display: flex;
  align-items: end;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}

.cheque-wrapper {
  display: flex;
  justify-content: center;
}

.cheque-surface {
  width: 8.25in;
  height: 3.5in;
  border: 1px solid #000;
  padding: 20px 22px;
  position: relative;
  background: white;
  background: url("~@/assets/checkbg.png");
  box-sizing: border-box;
}

/* -------------------- HEADER AREA -------------------- */

.header-row {
  display: flex;
  justify-content: space-between;
  margin-top: -8px;
}

.payer-block {
  margin-left: 300px;
  text-align: center;
}

.payer-name {
  font-weight: bold;
  font-size: 16px;
  margin: 0;
}

.payer-address {
  font-size: 13px;
  margin: 0;
}

/* DATE + CHEQUE NUMBER BLOCK */
.date-block {
  text-align: right;
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: -6px;
}

.cheque-number {
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 2px;
}

.date-line .label {
  font-weight: bold;
}

.date-line .value {
  margin-left: 6px;
}

/* -------------------- PAYEE SECTION -------------------- */

.payee-line {
  display: flex;
  align-items: center;
  margin-top: 4px;
}

.payee-value {
  flex: 1;
  margin: 0 10px;
}

.amount-box {
  border: 1px solid #000;
  padding: 4px 10px;
  min-width: 120px;
  text-align: right;
}

/* -------------------- AMOUNT IN WORDS -------------------- */

.amount-words {
  display: flex;
  margin-top: 6px;
}

.underline {
  border-bottom: 1px solid #000;
}

/* -------------------- BANK SECTION -------------------- */

.bank-row {
  margin-top: 6px; /* shifted up slightly */
}

.bank-name {
  margin: 0;
  font-weight: bold;
  font-size: 15px;
}

.bank-address {
  font-size: 12px;
  margin: 0;
}

/* -------------------- FOOTER -------------------- */

.footer-row {
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
}

.signature-line {
  display: block;
  width: 200px;
  border-bottom: 1px solid black;
}

/* -------------------- MICR -------------------- */

.micr-row {
  position: absolute;
  bottom: 20px;
  width: 100%;
  text-align: center;
}

.micr {
  font-family: MICR;
  font-size: 26px;
  letter-spacing: 2px;
}

/* -------------------- PRINT STYLES -------------------- */

@media print {
  .cheque-print {
    width: 8.5in;
    height: 11in;
    padding: 20px;
    box-sizing: border-box;
  }

  .cheque-surface {
    width: 8.25in;
    height: 3.5in;
    background: white;
    border: 0;
    padding: 20px 22px;
    box-sizing: border-box;
  }
}
</style>
